import React, {useCallback, useContext} from 'react';
import removeShortcutMutation from './graphql/remove-shortcut.graphql';
import addShortcutMutation from './graphql/add-shortcut.graphql';
import loginMutation from './graphql/login.graphql';
import logoutMutation from './graphql/logout.graphql';
import twoFactorLogin from './graphql/two-factor-login.graphql';
import updateOptionsMutation from './graphql/update-options.graphql';
import addAuthenticatorMutation from './graphql/add-authenticator.graphql';
import twoFactorResetMutation from './graphql/two-factor-reset.graphql';
import emailLoginMutation from './graphql/email-login.graphql';
import emailLoginRequestMutation from './graphql/email-login-request.graphql';
import passkeyLoginRequestMutation from './graphql/passkey-login-request.graphql';
import passkeyLoginMutation from './graphql/passkey-login.graphql';
import passkeyRegisterRequestMutation from './graphql/passkey-register-request.graphql';
import passkeyRegisterMutation from './graphql/passkey-register.graphql';
import passkeyDeleteMutation from './graphql/passkey-delete.graphql';
import passkeyRenameMutation from './graphql/passkey-rename.graphql';
import {serializeCredential, prepareCreationOptions, prepareRequestOptions} from '../utils/webauthn';
import {
  Query,
  Mutation,
  User,
  usePingQueryQuery,
  useAddShortcutMutationMutation,
  useRemoveShortcutMutationMutation,
  useLoginMutationMutation,
  useLogoutMutationMutation,
  useUpdateOptionsMutationMutation,
  useTwoFactorLoginMutation,
  useAddAuthenticatorMutation,
  useTwoFactorResetMutation,
  useEmailLoginMutation,
  useEmailLoginRequestMutation,
  usePasskeyLoginRequestMutation,
  usePasskeyLoginMutation,
  usePasskeyRegisterRequestMutation,
  usePasskeyRegisterMutation,
  usePasskeyDeleteMutation,
  usePasskeyRenameMutation,
  useGetMeQueryQuery,
  TwoFactorResetMutationResult,
} from '../generated/graphql';
import {useQuery, useMutation, useApolloClient, from, FetchResult} from '@apollo/client';
import { useState, useEffect, useRef } from 'react';
//import { onError } from '@apollo/client/link/error';
//import { pingQuery } from '../graphql';
import { useToast } from "./toast";
import {progressToast} from "../utils/progress-toast";
import {Refresh} from "@mui/icons-material";
import { Button } from '@mui/material';
import {onError} from "@apollo/client/link/error";
import {Observable} from "@apollo/client/utilities";
import {refreshAuth, saveRefreshToken, clearRefreshToken, forceReloginOnce} from "./graphql/auth-refresh";

export const UserContext = React.createContext(null);

export function useUserContext(): UserContextValue {
  return useContext<UserContextValue>(UserContext);
}


interface UserContextValue {
  user: User | undefined;
  cookie: string|undefined;
  addToShortcuts: any;
  removeFromShortcuts: any;
  setOption: any;
  setDelete: any;
  getOption: (name,id,isSearch) => (null | any);
  hasOption: (name)=>boolean;
  isError: boolean;
  errorMessage: string|null;
  isLoading: boolean;
  isLoggedIn: boolean|null;
  doLogin: ({username, password}: { username: string; password: string }) => Promise<void>;
  doLogout: () => Promise<void>;
  hasPermission: (code) => (boolean);
  hasAuthenticatorCode: boolean;
  getAuthenticatorCode: (callback) => Promise<void>;
  do2FA: (code) => Promise<void>;
  setMarked2Fa: (value) => void;
  emailLogin: (code) => Promise<void>;
  reset2Fa: (callback) => Promise<void>;
  emailLoginRequest: (callback) => Promise<void>;
  doPasskeyLogin: () => Promise<void>;
  doPasskeyRegister: (name?: string) => Promise<void>;
  doPasskeyDelete: (id: string) => Promise<void>;
  doPasskeyRename: (id: string, name: string) => Promise<void>;
  activeChallengeCountInbound:number;
  activeChallengeCountOutbound:number;
  challengeCountInboundNew: number;
  challengeCountOutboundNew: number;
  setUserNotifications: (notifications: Array<Notification>)=>void;
  setActiveChallengeCounts: (any)=>void;
  notifyJsVersion: (version: number)=>void;
}

export const UserProvider = ({ children }) => {
  const [jsVersion,setJsVersion] = useState<number|null|undefined>(0);
  const [addShortcut] = useAddShortcutMutationMutation();
  const [removeShortcut] = useRemoveShortcutMutationMutation();
  const [login,loginObject] = useLoginMutationMutation();
  const [logout] = useLogoutMutationMutation();
  const [updateOptions] = useUpdateOptionsMutationMutation();
  const [twoFactorMutation,twoFactorMutationObject] = useTwoFactorLoginMutation();
  const [addAuthenticator,addAuthenticatorObject] = useAddAuthenticatorMutation();
  const [marked2Fa,setMarked2Fa] = useState(false);
  const [twoFactorResetFunc,twoFactorResetObject] = useTwoFactorResetMutation();
  const [emailLoginFunc,emailLoginObject] = useEmailLoginMutation();
  const [emailLoginRequestFunc,emailLoginRequestObject] = useEmailLoginRequestMutation();
  const [passkeyLoginRequestFunc,passkeyLoginRequestObject] = usePasskeyLoginRequestMutation();
  const [passkeyLoginFunc,passkeyLoginObject] = usePasskeyLoginMutation();
  const [passkeyRegisterRequestFunc,passkeyRegisterRequestObject] = usePasskeyRegisterRequestMutation();
  const [passkeyRegisterFunc,passkeyRegisterObject] = usePasskeyRegisterMutation();
  const [passkeyDeleteFunc,passkeyDeleteObject] = usePasskeyDeleteMutation();
  const [passkeyRenameFunc,passkeyRenameObject] = usePasskeyRenameMutation();
  const [wasError,setWasError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [permissions,setPermissions] = useState({});
  const [localData,setLocalData] = useState({
    activeChallengeCountInbound:0,
    activeChallengeCountOutbound:0,
    challengeCountInboundNew:0,
    challengeCountOutboundNew:0,
    messages:[]});
  const setActiveChallengeCounts = useCallback((newValues)=>{
    setLocalData((currentData)=>{
      const p = {...currentData};
      p.activeChallengeCountInbound = newValues.activeChallengeCountInbound;
      p.activeChallengeCountOutbound = newValues.activeChallengeCountOutbound;
      p.challengeCountInboundNew = newValues.challengeCountInboundNew;
      p.challengeCountOutboundNew = newValues.challengeCountOutboundNew;
      return p;
    });
  },[]);
  const setUserNotifications = useCallback((newValues)=>{
    // this is called mostly from the websocket
    // takes an array of updated values, recalculates the number for inbound/outbound yellow badges
    setLocalData((currentData)=>{
      const p = {...currentData};
      p.challengeCountOutboundNew = 0;
      p.challengeCountInboundNew = 0;
      p.messages = [...currentData.messages];
      newValues.forEach((newValue)=>{
        let theIdx = -1;
        p.messages.find((v,idx)=>{if(v.id==newValue.id) { theIdx = idx; return true; } return false; });
        if(theIdx>-1) {
          p.messages[theIdx] = newValue;
        } else {
          p.messages.push(newValue);
        }
      });
      console.log('User notifications are',p,'from',newValues);
      // recalculate the numbers
      p.messages.forEach((v)=>{
        switch(v.type) {
          case 'challenge_received_own':
            if(v.status!='viewed-challenge') {
              p.challengeCountInboundNew++;
            }
          break;
          case 'challenge_updated_own':
            if(v.status!='viewed-challenge') {
              p.challengeCountOutboundNew++;
            }
          break;
        }
        
      });
      console.log('SUNP',p);
      return p;
    });
  },[]);
  const { toast } = useToast();
  const ping = usePingQueryQuery();
    const apolloClient = useApolloClient();
    // Ref to the latest getMe.refetch so the auth-error link (set up once) can
    // force a re-login when a token refresh fails.
    const getMeRefetchRef = useRef<() => Promise<any>>(() => Promise.resolve());
  // Baseline JS version: set from the one-shot ping on load (catches deploys
  // that happened while this client was offline), then updated in real time
  // via the JsVersionUpdated websocket event.
  const jsVersionRef = useRef<number>(0);
  const notifyJsVersion = useCallback((newVersion: number)=>{
    if(!newVersion) return;
    if(!jsVersionRef.current) {
      jsVersionRef.current = newVersion;
      setJsVersion(newVersion);
      return;
    }
    if(jsVersionRef.current != newVersion) {
      toast('A new version of Daybook is available, please save any outstanding changes then refresh',{
        variant:"warning",
        id:'DB-UPDATE',
        action:<Button size="small" onClick={()=>{ document.location.reload();} }><Refresh/>Refresh now</Button>,

        timeout: 0
      });
    }
  },[toast]);
  useEffect(()=>{
    if(ping.data?.ping?.JSVersion) {
      notifyJsVersion(ping.data.ping.JSVersion);
    }
  },[ping.data?.ping?.JSVersion,notifyJsVersion]);
  console.log('site version',jsVersion);

  useEffect(()=>{
    console.log('LINK CHANGE');
    apolloClient.setLink(from([
      onError((errors)=> {
        console.log('LINK ERROR',errors);
        if(!errors.graphQLErrors || !errors.graphQLErrors[0]) { return; }
        const isUnauthenticated = errors.graphQLErrors.some((e)=> e.message?.startsWith('Unauthenticated'));
        if(!isUnauthenticated) { return; }
        // Access token has expired: refresh it, then replay the original operation.
        // The replay goes straight to the upload link (not back through this handler),
        // so there is no risk of an infinite refresh loop.
        return new Observable<FetchResult>((observer)=>{
          let sub: any;
          refreshAuth().then((ok)=>{
            if(!ok) {
              // Refresh failed: clear the stale "logged in" state so the login screen shows.
              // forceReloginOnce() guards against the refetch re-entering this handler
              // and storming the server for the rest of the dead-token episode.
              if(forceReloginOnce()) {
                getMeRefetchRef.current();
              }
              observer.error(new Error('Session expired, please log in again'));
              return;
            }
            sub = errors.forward(errors.operation).subscribe({
              next: (result)=> {
                // If the replay is still unauthenticated, force a re-login (at most once).
                if(result.errors?.some((e:any)=> e.message?.startsWith('Unauthenticated'))) {
                  if(forceReloginOnce()) {
                    getMeRefetchRef.current();
                  }
                }
                observer.next(result);
              },
              error: (err)=> observer.error(err),
              complete: ()=> observer.complete(),
            });
          }).catch((e)=>{
            observer.error(e);
          });
          return ()=>{ if(sub) { sub.unsubscribe(); } };
        });
      }),
       apolloClient.link]));
  },[]);

  const getMe = useGetMeQueryQuery({fetchPolicy: "network-only", errorPolicy: 'none',onError:(e)=>{ setWasError(e); },onCompleted:()=>{setWasError(null);}});
  console.log('re-render with',getMe,wasError);
  useEffect(() => {
    getMeRefetchRef.current = getMe.refetch;
  });

  useEffect(() => {
    if(getMe?.data?.me) {
      const newPermissions = {};
      const rawPermissions = getMe.data.me?.role?.permissions;
      rawPermissions?.forEach((v)=>{
        if(!v) return;
        newPermissions[v.code??''] = true;
        if(v.enable_section) {
          newPermissions['section.'+v.enable_section] = true;
        }
      });
      setPermissions(newPermissions);
      setUserNotifications(getMe.data.me?.userNotifications);
      setActiveChallengeCounts(getMe.data.me);
    } else {
      setPermissions({});
    }
    if(!getMe?.loading) {
      setIsLoggedIn(!!getMe?.data?.me);
    }
    console.log('toggling logged in by',getMe.data,'to',!!getMe?.data?.me);
  }, [getMe.loading,getMe?.data?.me]);

  const addToShortcuts = (id) => {
    return addShortcut({ variables: { id: id } });
  };
  const removeFromShortcuts = (id) => {
    return removeShortcut({ variables: { id: id } });
  };

  const setMarked2FaWrapper = useCallback((value) => {
    getMe.refetch();
    setMarked2Fa(value);
  },[]);

  const doLogin = useCallback(async ({ username, password }) => {
    if(loginObject.loading) return;
    const t = progressToast(toast)('LOGIN');
    t.start('Logging in');
    try {
      const loginResult = await login({ variables: { username: username, password: password } });
      if (loginResult?.data?.login?.refresh_token) {
        saveRefreshToken(loginResult.data.login.refresh_token);
      }
      // this returns a TOKEN, we need to fetch the user details separately...
      try {
        await getMe.refetch();
        setWasError(null);
        t.ok('Logged in');
      } catch (e) {
        setWasError(e);
        if (e.message == '2FA-REQUIRED') {
          t.ok('Logging succeeded, proceed to 2FA');
        } else {
          t.fail(e.message);
        }
      }
    } catch (e) {
      t.fail(e.message=='invalid_grant'?'Login Failed':e.message);
    }
  }, [getMe.data?.me, login,loginObject.loading]);
  
  const doLogout = useCallback(async () => {
    console.log('UserProvider - logging out');
    await logout();
    clearRefreshToken();
    toast("Logged out",{id:'LOGOUT',variant:'success'});
    // fetch the busted user details /shrug
    getMe.refetch();
  }, [getMe.data?.me, logout]);


  // const getOption = useCallback((name) => {
  //   if (!isLoggedIn) return null;
  //   let value = null;
  //   const user = getMe.data.me;
  //   user.options.forEach((v) => {
  //     if (v.name === name) {
  //       value = v.value;
  //       console.log('found?',value);
  //     }
  //   }
  //   );
  //   if (!value) { return value; }
  //   return JSON.parse(value);
  // }, [getMe.data?.me, isLoggedIn]);
  const hasOption = useCallback((name:string):boolean => {
    if (!isLoggedIn) return false;
    const user = getMe?.data?.me;
    if(!user?.options) return false;
    return !!user?.options?.find((v) => v?.name==name);
  },[getMe.data?.me, isLoggedIn]);

  const getOption = useCallback((name, selectedId,isSearch=false) => {
    // options are currently only used for search prefs and have a specialized meaning
    if (!isLoggedIn) return null;
    const user = getMe?.data?.me;
    if(!user?.options) return null;
    if(isSearch) {
      let value:string|null|undefined = null;
      let searchName:string|null|undefined = null;
      let searchNameConcat:string|null = null;
      const searchNameArray: any[] = [];
      console.log("user?",user!.options);
      // Pop up the search dropdown
      user.options.forEach((v) => {
        if (v?.name === name) {
          searchNameConcat = v?.id + '.' + v?.search_name;
          searchNameArray.push(searchNameConcat);
        }
      });
      const returnValue = {value:null,searchNameArray:searchNameArray,searchName:searchName };
      // Find the option based on the selectedId
      if (selectedId) {
        const selectedOption = user.options.find((option) => option?.id === selectedId);
        if(!selectedOption) return returnValue;
        value = selectedOption.value;
        searchName = selectedOption.search_name;
      } else {
        // this will default to the first option, which is no longer intended (2025-06-24)
        /*user?.options?.forEach((v) => {
          if (v?.name === name) {
            value = v?.value;
            searchName = v?.search_name;
          }
        }
        );*/
        // instead add a special case of 'default' which performs the same action
        const selectedOption = user.options.find((option)=>option?.search_name=='default');
        if(!selectedOption) return returnValue;
        value = selectedOption.value;
        searchName = selectedOption.search_name;
      }
      returnValue.value = value?JSON.parse(value):null;
      return returnValue;
    } else {
      return null; // meh?
    }
  }, [getMe.data?.me, isLoggedIn]);

  const do2Fa = useCallback(async (code) => {
    if(twoFactorMutationObject.loading) return;
    try {
      const result = await twoFactorMutation({ variables: { code: code } });
      if(!result?.data?.twoFactorLogin?.success) {
        throw 'Fail';
      } else {
        await getMe.refetch();
      }
    } catch (e) {
      console.log('error is',e);
      toast('2 Factor login incorrect',{variant:'error'});
    }
    // this returns a TOKEN, we need to fetch the user details separately...
  }, [twoFactorMutationObject.loading]);

  // generates a new authenticator code
  const getAuthenticatorCode = useCallback(async (callback) => {
    if(addAuthenticatorObject.loading) return;
    const data = await addAuthenticator();
    callback(data?.data?.addAuthenticatorCode?.code);
  }, [addAuthenticatorObject.loading]);

  // sends a code to the users's email
  const emailLoginRequest = useCallback(async (callback) => {
    if(emailLoginRequestObject.loading) return;
    const data = await emailLoginRequestFunc();
    callback(data?.data?.emailLoginRequest);
  }, [emailLoginRequestObject.loading]);

  // confirms whether the code sent by email is correct
  const emailLogin = useCallback(async (code) => {
    if(emailLoginObject.loading) return;
    try {
      await emailLoginFunc({ variables: { code: code } });
      await getMe.refetch();
    } catch (e) {
      toast('2 Factor login incorrect',{id:'LOGIN_2FA_FAIL',variant:'error'});
    }
  }, [emailLoginObject.loading]);

  // generates a reset code
  const reset2Fa = useCallback(async (callback) => {
    if(twoFactorResetObject.loading) return;
    let data:any|null = null;
    try {
      data = await twoFactorResetFunc();
    } catch (e) {
      toast('Unable to generate reset email',{id:'LOGIN_2FA_FAIL',variant:'error'});
      return;
    }
    callback(data?.data?.twoFactorReset);
  }, [twoFactorResetObject.loading]);

  // passwordless passkey login: request a challenge, prompt the browser, submit the credential
  const doPasskeyLogin = useCallback(async () => {
    if(passkeyLoginRequestObject.loading || passkeyLoginObject.loading) return;
    const t = progressToast(toast)('PASSKEY LOGIN');
    t.start('Waiting for passkey');
    try {
      const reqResult = await passkeyLoginRequestFunc();
      const options = reqResult?.data?.passkeyLoginRequest?.options;
      if(!options) throw new Error('No passkey options returned');
      const credential = await navigator.credentials.get({ publicKey: prepareRequestOptions(options) }) as PublicKeyCredential | null;
      if(!credential) throw new Error('No passkey provided');
      const loginResult = await passkeyLoginFunc({ variables: { credential: serializeCredential(credential) } });
      if(loginResult?.data?.passkeyLogin?.refresh_token) {
        saveRefreshToken(loginResult.data.passkeyLogin.refresh_token);
      }
      await getMe.refetch();
      setWasError(null);
      t.ok('Logged in');
    } catch (e:any) {
      console.error('passkey login error', e);
      t.fail(e?.message || 'Passkey login failed');
    }
  }, [passkeyLoginRequestObject.loading, passkeyLoginObject.loading]);

  // register a new passkey for the current user
  const doPasskeyRegister = useCallback(async (name?: string) => {
    if(passkeyRegisterRequestObject.loading || passkeyRegisterObject.loading) return;
    const t = progressToast(toast)('PASSKEY REGISTER');
    t.start('Waiting for passkey');
    try {
      const reqResult = await passkeyRegisterRequestFunc({ variables: { name } });
      const options = reqResult?.data?.passkeyRegisterRequest?.options;
      if(!options) throw new Error('No passkey options returned');
      const credential = await navigator.credentials.create({ publicKey: prepareCreationOptions(options) }) as PublicKeyCredential | null;
      if(!credential) throw new Error('No passkey created');
      await passkeyRegisterFunc({ variables: { credential: serializeCredential(credential), name } });
      await getMe.refetch();
      t.ok('Passkey added');
    } catch (e:any) {
      console.error('passkey register error', e);
      t.fail(e?.message || 'Passkey registration failed');
    }
  }, [passkeyRegisterRequestObject.loading, passkeyRegisterObject.loading]);

  // delete a passkey
  const doPasskeyDelete = useCallback(async (id: string) => {
    if(passkeyDeleteObject.loading) return;
    try {
      await passkeyDeleteFunc({ variables: { id } });
      await getMe.refetch();
      toast('Passkey removed',{variant:'success'});
    } catch (e:any) {
      toast(e?.message || 'Unable to remove passkey',{variant:'error'});
    }
  }, [passkeyDeleteObject.loading]);

  // rename a passkey
  const doPasskeyRename = useCallback(async (id: string, name: string) => {
    if(passkeyRenameObject.loading) return;
    try {
      await passkeyRenameFunc({ variables: { id, name } });
      await getMe.refetch();
      toast('Passkey renamed',{variant:'success'});
    } catch (e:any) {
      toast(e?.message || 'Unable to rename passkey',{variant:'error'});
    }
  }, [passkeyRenameObject.loading]);

  const setOption = useCallback(async (name, value, searchName, setToastMessage, setOpenSnackbar) => {
    const send:{update:any[],create:any[]} = { update: [], create: []};
    if (!isLoggedIn) return null;
    const user = getMe?.data?.me;
    if(!user?.options) return;
    const existingOption = user.options.find((option) => option?.search_name === searchName);
    console.log("existing record", existingOption);
    
    try {
      if (existingOption) {
        // Option with the given name already exists, update it
        const updatedOption = {
          id: existingOption.id,
          name: name,
          value: JSON.stringify(value),
          search_name: searchName
        };
        send.update.push(updatedOption);
        setToastMessage('Search updated successfully');
        setOpenSnackbar(true);
      } 
      else {
        // Option with the given name doesn't exist, create a new one
        const newOption = {
          name: name,
          value: JSON.stringify(value),
          search_name: searchName
        };
        send.create.push(newOption);
        setToastMessage('Search saved successfully');
        setOpenSnackbar(true);
      }
      const response = await updateOptions({ variables: { options: send } });
      return response;
    } catch (error) {
      console.error("Error updating options:", error);
    }
  }, [getMe.data?.me, isLoggedIn, updateOptions]);

  // Delete searches using option delete
  const setDelete = useCallback(async(name, id, setOpenSnackbar, setToastMessage) => {
      const send:{delete:string[]} = {delete: [] };
      const user = getMe?.data?.me;
      if(!user) return null;
      const existingOption = user.options?.find((option) => option?.id === id && option?.name === name);
  
      if (existingOption) {
        send.delete.push(id);
        setToastMessage('Search deleted successfully');
        setOpenSnackbar(true);
      }
      else {
        setToastMessage('Delete unsuccessful');
        setOpenSnackbar(true);
      }
      const response = await updateOptions({ variables: { options: send } });
      return response;
  }, [getMe.data?.me, isLoggedIn, updateOptions]);
  
  

  const hasPermission = useCallback((code) => {
    // disable campaign challenges 'hard'
    //if(code=='campaigns.challenges') return false;
    if (!isLoggedIn) return false;
    return !!permissions[code];
/*
    var user = getMe.data.me;
    var allowed = false;
    user.role.permissions.forEach((permission) => {
      if (permission.code === code) { allowed = true; }
    });
    return allowed;*/
  }, [permissions, isLoggedIn]);
console.log('localData',localData);
const args: UserContextValue = {
  user: getMe?.data?.me as any,
  cookie: getMe?.data?.me?.token,
  addToShortcuts: addToShortcuts,
  removeFromShortcuts: removeFromShortcuts,
  setOption: setOption,
  setDelete: setDelete,
  getOption: getOption,
  hasOption: hasOption,
  isError: !!getMe.error,
  errorMessage: getMe.error?.message??'',
  isLoading: getMe.loading,
  isLoggedIn: isLoggedIn,
  doLogin: doLogin,
  doLogout: doLogout,
  hasPermission: hasPermission,
  hasAuthenticatorCode: getMe.data?.me?.hasAuthenticatorQrCode||marked2Fa,
  getAuthenticatorCode: getAuthenticatorCode,
  do2FA: do2Fa,
  setMarked2Fa: setMarked2FaWrapper,
  emailLogin: emailLogin,
  reset2Fa: reset2Fa,
  emailLoginRequest: emailLoginRequest,
  doPasskeyLogin: doPasskeyLogin,
  doPasskeyRegister: doPasskeyRegister,
  doPasskeyDelete: doPasskeyDelete,
  doPasskeyRename: doPasskeyRename,
  activeChallengeCountInbound: localData.activeChallengeCountInbound,
  activeChallengeCountOutbound: localData.activeChallengeCountOutbound,
  challengeCountInboundNew: localData.challengeCountInboundNew,
  challengeCountOutboundNew: localData.challengeCountOutboundNew,
  setUserNotifications: setUserNotifications,
  setActiveChallengeCounts: setActiveChallengeCounts,
  notifyJsVersion: notifyJsVersion
};

  return (<UserContext.Provider
    value={args}
  >
    {children}
   
  </UserContext.Provider>
  );

};
